import { createContext, useContext, useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { getDefaultPanelPath } from "@/lib/authz";

const AuthContext = createContext(null);
const ACTIVE_USER_ID_KEY = "obihub_active_user_id";
const USER_CACHE_KEY = "obihub_user_cache";
const trustedDeviceKey = (email) =>
  `obihub_trusted_device_token:${String(email || "").trim().toLowerCase()}`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const enrichUserWithPanelSlug = async (baseUser) => {
    const userData = { ...(baseUser || {}) };
    return userData;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("obihub_token");
    if (!token) {
      localStorage.removeItem(ACTIVE_USER_ID_KEY);
      setLoading(false);
      return;
    }

    try {
      const userDataRaw = await api.get("/auth/me");
      const userData = await enrichUserWithPanelSlug(userDataRaw);
      setUser(userData);
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData || {}));
      if (userData?.id) {
        localStorage.setItem(ACTIVE_USER_ID_KEY, String(userData.id));
      } else {
        localStorage.removeItem(ACTIVE_USER_ID_KEY);
      }
    } catch (error) {
      const status = Number(error?.status || 0);
      if (status === 401) {
        localStorage.removeItem("obihub_token");
        localStorage.removeItem(ACTIVE_USER_ID_KEY);
        localStorage.removeItem(USER_CACHE_KEY);
        setUser(null);
      } else {
        try {
          const cached = JSON.parse(localStorage.getItem(USER_CACHE_KEY) || "null");
          if (cached && typeof cached === "object") {
            setUser(cached);
          }
        } catch {
          // ignore cache parse errors
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const userDataRaw = await api.get("/auth/me");
      const userData = await enrichUserWithPanelSlug(userDataRaw);
      setUser(userData);
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData || {}));
      if (userData?.id) {
        localStorage.setItem(ACTIVE_USER_ID_KEY, String(userData.id));
      } else {
        localStorage.removeItem(ACTIVE_USER_ID_KEY);
      }
      return userData;
    } catch {
      localStorage.removeItem(ACTIVE_USER_ID_KEY);
      return null;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      const mergedUserRaw = {
        ...(response.user || {}),
        role: response.role || response.user?.role,
        permissions: response.permissions || response.user?.permissions,
      };
      const mergedUser = await enrichUserWithPanelSlug(mergedUserRaw);
      localStorage.setItem("obihub_token", response.access_token);
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(mergedUser || {}));
      if (mergedUser?.id) {
        localStorage.setItem(ACTIVE_USER_ID_KEY, String(mergedUser.id));
      } else {
        localStorage.removeItem(ACTIVE_USER_ID_KEY);
      }
      setUser(mergedUser);
      toast.success("Giriş başarılı!");
      return { success: true, redirectPath: getDefaultPanelPath(mergedUser) };
    } catch (error) {
      toast.error(error.message || "Giriş başarısız");
      return { success: false, error: error.message };
    }
  };

  const requestLoginCode = async (email, password, turnstileToken) => {
    try {
      const normalizedEmail = String(email || "").trim().toLowerCase();
      const response = await api.post("/auth/login/request-code", {
        email,
        password,
        turnstile_token: turnstileToken,
        device_token: localStorage.getItem(trustedDeviceKey(normalizedEmail)) || "",
      });
      if (response?.access_token && response?.user) {
        const mergedUserRaw = {
          ...(response.user || {}),
          role: response.role || response.user?.role,
          permissions: response.permissions || response.user?.permissions,
        };
        const mergedUser = await enrichUserWithPanelSlug(mergedUserRaw);
        localStorage.setItem("obihub_token", response.access_token);
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(mergedUser || {}));
        if (mergedUser?.id) {
          localStorage.setItem(ACTIVE_USER_ID_KEY, String(mergedUser.id));
        } else {
          localStorage.removeItem(ACTIVE_USER_ID_KEY);
        }
        setUser(mergedUser);
        toast.success(response?.message || "Giriş başarılı!");
        return {
          success: true,
          skipVerification: true,
          redirectPath: getDefaultPanelPath(mergedUser),
        };
      }
      toast.success(response?.message || "Doğrulama kodu gönderildi");
      return { success: true, expiresIn: Number(response?.expires_in || 0), skipVerification: false };
    } catch (error) {
      toast.error(error.message || "Kod gönderilemedi");
      return { success: false, error: error.message };
    }
  };

  const verifyLoginCode = async (email, code) => {
    try {
      const response = await api.post("/auth/login/verify-code", { email, code });
      const mergedUserRaw = {
        ...(response.user || {}),
        role: response.role || response.user?.role,
        permissions: response.permissions || response.user?.permissions,
      };
      const mergedUser = await enrichUserWithPanelSlug(mergedUserRaw);
      localStorage.setItem("obihub_token", response.access_token);
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(mergedUser || {}));
      if (mergedUser?.id) {
        localStorage.setItem(ACTIVE_USER_ID_KEY, String(mergedUser.id));
      } else {
        localStorage.removeItem(ACTIVE_USER_ID_KEY);
      }
      const trustedToken = String(response?.trusted_device_token || "").trim();
      if (trustedToken) {
        localStorage.setItem(trustedDeviceKey(email), trustedToken);
      }
      setUser(mergedUser);
      toast.success("Giriş başarılı!");
      return { success: true, redirectPath: getDefaultPanelPath(mergedUser) };
    } catch (error) {
      toast.error(error.message || "Kod doğrulanamadı");
      return { success: false, error: error.message };
    }
  };

  const register = async (data) => {
    try {
      const response = await api.post("/auth/register", data);
      const mergedUserRaw = {
        ...(response.user || {}),
        role: response.role || response.user?.role,
        permissions: response.permissions || response.user?.permissions,
      };
      const mergedUser = await enrichUserWithPanelSlug(mergedUserRaw);
      localStorage.setItem("obihub_token", response.access_token);
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(mergedUser || {}));
      if (mergedUser?.id) {
        localStorage.setItem(ACTIVE_USER_ID_KEY, String(mergedUser.id));
      } else {
        localStorage.removeItem(ACTIVE_USER_ID_KEY);
      }
      setUser(mergedUser);
      toast.success("Kayıt başarılı!");
      return { success: true, redirectPath: "/onboarding" };
    } catch (error) {
      toast.error(error.message || "Kayıt başarısız");
      return { success: false, error: error.message };
    }
  };

  const requestRegisterCode = async (email, turnstileToken) => {
    try {
      const response = await api.post("/auth/register/request-code", {
        email,
        turnstile_token: turnstileToken,
      });
      toast.success(response?.message || "Kayıt doğrulama kodu gönderildi");
      return { success: true, expiresIn: Number(response?.expires_in || 0) };
    } catch (error) {
      toast.error(error.message || "Kod gönderilemedi");
      return { success: false, error: error.message };
    }
  };

  const requestPasswordResetCode = async (email) => {
    try {
      const response = await api.post("/auth/password-reset/request-code", { email });
      toast.success(response?.message || "Doğrulama kodu gönderildi");
      return { success: true, expiresIn: Number(response?.expires_in || 0) };
    } catch (error) {
      toast.error(error.message || "Kod gönderilemedi");
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email, code, newPassword) => {
    try {
      const response = await api.post("/auth/password-reset/confirm", {
        email,
        code,
        new_password: newPassword,
      });
      toast.success(response?.message || "Şifre güncellendi");
      return { success: true };
    } catch (error) {
      toast.error(error.message || "Şifre güncellenemedi");
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("obihub_token");
    localStorage.removeItem(ACTIVE_USER_ID_KEY);
    localStorage.removeItem(USER_CACHE_KEY);
    setUser(null);
    toast.success("Çıkış yapıldı");
  };

  const startParasutAuth = async (mode = "login") => {
    const isLocal = String(window.location.hostname || "").toLowerCase() === "localhost";
    const payload = isLocal
      ? { mode, redirect_uri: `${window.location.origin}/auth/parasut/callback` }
      : { mode };
    const response = await api.post("/auth/parasut/start", payload);
    if (!response?.auth_url) {
      throw new Error("Paraşüt giriş bağlantısı üretilemedi");
    }
    return response.auth_url;
  };

  const startParasutConnect = async () => {
    const isLocal = String(window.location.hostname || "").toLowerCase() === "localhost";
    const payload = isLocal
      ? { redirect_uri: `${window.location.origin}/auth/parasut/callback` }
      : {};
    const response = await api.post("/parasut/connect/start", payload);
    if (!response?.auth_url) {
      throw new Error("Paraşüt bağlantı bağlantısı üretilemedi");
    }
    return response.auth_url;
  };

  const completeParasutAuth = async (code, state) => {
    const response = await api.post("/auth/parasut/exchange", { code, state });
    const mergedUserRaw = {
      ...(response.user || {}),
      role: response.role || response.user?.role,
      permissions: response.permissions || response.user?.permissions,
    };
    const mergedUser = await enrichUserWithPanelSlug(mergedUserRaw);
    localStorage.setItem("obihub_token", response.access_token);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(mergedUser || {}));
    if (mergedUser?.id) {
      localStorage.setItem(ACTIVE_USER_ID_KEY, String(mergedUser.id));
    } else {
      localStorage.removeItem(ACTIVE_USER_ID_KEY);
    }
    setUser(mergedUser);
    return { success: true, redirectPath: getDefaultPanelPath(mergedUser) };
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      requestLoginCode,
      verifyLoginCode,
      register,
      requestRegisterCode,
      requestPasswordResetCode,
      resetPassword,
      logout,
      refreshUser,
      startParasutAuth,
      startParasutConnect,
      completeParasutAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
